/**
 * Lists the organizations the signed-in user belongs to.
 *
 * Deliberately a direct GitHub call rather than a backend hop. This is the
 * caller's own membership, it needs only `read:org` (a scope the login grant
 * keeps), and it runs during onboarding before a workspace or a stored token
 * exists, which a server-side read cannot satisfy. Repository data is the
 * opposite case and goes through the backend.
 */
/**
 * `id` is the provider account id. It has to survive this hop: the workspace
 * stores it as `GitOrgId` and the App install link passes it as
 * `suggested_target_id`, which is what stops GitHub showing an account picker.
 */
export type GithubOrgSummary = { login?: string; id?: number };

/** GitHub's maximum page size for `/user/orgs`. */
const ORGS_PER_PAGE = 100;

/** Page ceiling, so a pathological account cannot loop forever. */
const MAX_ORG_PAGES = 20;

const hasNextPage = (linkHeader: string | null): boolean =>
  !!linkHeader && linkHeader.includes('rel="next"');

/**
 * Fetches every organization the token's owner is a member of, following
 * GitHub's `Link` pagination.
 *
 * @param accessToken - The session's GitHub token.
 * @returns The orgs, or an empty array when no token is available.
 * @throws Error with humane copy when GitHub rejects the request.
 */
export async function fetchUserOrgs(
  accessToken: string
): Promise<GithubOrgSummary[]> {
  if (!accessToken) {
    // Distinct from "you belong to no orgs": callers render a different empty
    // state for each, and silently returning [] hid a missing token before.
    throw new Error('Sign in with GitHub again to load your organizations.');
  }

  const orgs: GithubOrgSummary[] = [];

  for (let page = 1; page <= MAX_ORG_PAGES; page += 1) {
    const response = await fetch(
      `https://api.github.com/user/orgs?per_page=${ORGS_PER_PAGE}&page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/vnd.github+json',
        },
      }
    );

    if (!response.ok) {
      // Scopes are the usual culprit for a 403 here, so log them: without
      // read:org GitHub returns only publicly visible memberships.
      console.error('GitHub /user/orgs failed', {
        status: response.status,
        scopes: response.headers.get('X-OAuth-Scopes'),
      });
      throw new Error(
        'We could not load your GitHub organizations. Please try again in a moment.'
      );
    }

    const batch = (await response.json()) as GithubOrgSummary[];
    if (!Array.isArray(batch)) break;
    orgs.push(...batch);

    if (page === 1 && batch.length === 0) {
      // An empty first page with a working token means the grant cannot see the
      // memberships, which reads as "no orgs" in the UI. Log the scopes so that
      // case is distinguishable from genuinely belonging to none.
      console.warn('GitHub /user/orgs returned no organizations', {
        scopes: response.headers.get('X-OAuth-Scopes'),
      });
    }

    if (!hasNextPage(response.headers.get('Link'))) break;
  }

  return orgs;
}
